import React from "react";
import { Box, CircularProgress, Tooltip } from "@mui/joy";
import DarkInput from "../common/DarkInput";
import DarkTypography from "../common/DarkTypography";
import { useLocation, useNavigate } from "react-router-dom";
import type { LoginForm } from "../../api/auth/Auth.typed";
import { useAuth } from "../../contexts/api";
import { useAlert } from "../../contexts/app/notificationToast";
import {
  SSave,
  VisibilityIcon as Visibility,
  VisibilityOffIcon as VisibilityOff,
} from "../../styles/StyledMUI";
import IconButtonCTA from "../common/IconButtonCTA";
import { colorTokens, withAlpha } from "../../styles/globalStyles";
import { useZekayAuthMark } from "../../utils/useZekayAuthMark";

export default function CreateInvitedUserPage() {
  const { src: zekayLogoSrc, onImgError: onZekayLogoError } = useZekayAuthMark();
  const [password1, setPassword1] = React.useState<string>("");
  const [password2, setPassword2] = React.useState<string>("");
  const [isValid, setIsValid] = React.useState<boolean>(false);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const { verifyToken, createInvitedUser } = useAuth();
  const { showAlert } = useAlert();
  const location = useLocation();
  const navigate = useNavigate();
  const [showPwd1, setShowPwd1] = React.useState<boolean>(false);
  const [showPwd2, setShowPwd2] = React.useState<boolean>(false);

  const token = new URLSearchParams(location.search).get("token");
  const existing = new URLSearchParams(location.search).get("existing");

  const handleInvite = () => {
    if (password1.length >= 8) {
      if (token) {
        if (password1 === password2) {
          const h: LoginForm = {
            Token: token,
            Password: password2,
          };

          setIsLoading(true);
          createInvitedUser(h)
            .then(() => navigate("/login?invited=created"))
            .catch(() => {
              showAlert("Une erreur est survenue", "danger");
            })
            .finally(() => setIsLoading(false));
        } else {
          showAlert("Les mots de passe ne sont pas identiques", "danger");
        }
      } else {
        showAlert("Lien invalide, veuillez ré-ouvrir le lien envoyé par mail", "danger");
      }
    } else {
      showAlert("La longueur du mot de passe est inférieure à 8 caractères", "danger");
    }
  };

  const handleAcceptExisting = React.useCallback(() => {
    if (!token) {
      showAlert("Lien invalide, veuillez ré-ouvrir le lien envoyé par mail", "danger");
      return;
    }
    const h: LoginForm = { Token: token };
    setIsLoading(true);
    createInvitedUser(h)
      .then(() => navigate("/login?invited=linked"))
      .catch((err: any) => {
        const msg = String(err?.message || "");
        if (msg.startsWith("HTTP_403") || msg.includes("Accès refusé")) {
          showAlert("Invitation expirée ou invalide; demandez une nouvelle invitation.", "danger");
        } else {
          showAlert(
            msg.trim() || "Une erreur est survenue, veuillez réessayer ultérieurement.",
            "danger",
          );
        }
      })
      .finally(() => setIsLoading(false));
  }, [createInvitedUser, navigate, showAlert, token]);

  React.useEffect(() => {
    const token = new URLSearchParams(location.search).get("token");

    if (token) {
      const h: LoginForm = {
        Token: token,
      };

      verifyToken(h).then((r) => setIsValid(!!r));
    } else {
      setIsValid(false);
    }
  }, [location.search, verifyToken]);

  // Auto-accept flow for existing account
  React.useEffect(() => {
    if (isValid && existing === "1" && !isLoading) {
      handleAcceptExisting();
    }
  }, [existing, handleAcceptExisting, isLoading, isValid]);

  return (
    <Box
      sx={{
        height: "100dvh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        overflowX: "hidden",
        padding: 2,
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 400,
          textAlign: "center",
          padding: 3,
          borderRadius: 2,
          boxShadow: `0 4px 12px ${withAlpha(colorTokens.bg.overlay, 0.1)}`,
        }}
      >
        {/* Logo */}
        <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
          <img src={zekayLogoSrc} alt="Logo" width="200px" onError={onZekayLogoError} />
        </Box>

        {isValid ? (
          <>
            <DarkTypography level="h4" sx={{ marginBottom: 2 }}>
              Bienvenue !
            </DarkTypography>
            {existing === "1" ? (
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
                <Tooltip title="Accepter l'invitation">
                  <span>
                    <IconButtonCTA
                      ctaVariant="success"
                      aria-label="Accepter invitation"
                      variant="plain"
                      onClick={handleAcceptExisting}
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <CircularProgress size="sm" />
                      ) : (
                        <SSave sx={{ color: colorTokens.status.success }} />
                      )}
                    </IconButtonCTA>
                  </span>
                </Tooltip>
              </Box>
            ) : (
              <>
                <DarkInput
                  placeholder="Mot de passe"
                  type={showPwd1 ? "text" : "password"}
                  value={password1}
                  onChange={(e) => setPassword1(e.target.value)}
                  endDecorator={
                    <IconButtonCTA
                      ctaVariant="neutral"
                      aria-label="Afficher/masquer mot de passe"
                      size="sm"
                      onClick={() => setShowPwd1((v) => !v)}
                    >
                      {showPwd1 ? <VisibilityOff /> : <Visibility />}
                    </IconButtonCTA>
                  }
                  sx={{ marginY: 1 }}
                />
                <DarkInput
                  placeholder="Confirmez le mot de passe"
                  type={showPwd2 ? "text" : "password"}
                  value={password2}
                  onChange={(e) => setPassword2(e.target.value)}
                  endDecorator={
                    <IconButtonCTA
                      ctaVariant="neutral"
                      aria-label="Afficher/masquer confirmation mot de passe"
                      size="sm"
                      onClick={() => setShowPwd2((v) => !v)}
                    >
                      {showPwd2 ? <VisibilityOff /> : <Visibility />}
                    </IconButtonCTA>
                  }
                  sx={{ marginY: 1 }}
                />
                <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
                  <Tooltip title="Créer le compte">
                    <span>
                      <IconButtonCTA
                        ctaVariant="success"
                        aria-label="Créer compte invité"
                        variant="plain"
                        onClick={handleInvite}
                        disabled={isLoading}
                      >
                        {isLoading ? (
                          <CircularProgress size="sm" />
                        ) : (
                          <SSave sx={{ color: colorTokens.status.success }} />
                        )}
                      </IconButtonCTA>
                    </span>
                  </Tooltip>
                </Box>
              </>
            )}
          </>
        ) : (
          <>
            <DarkTypography level="h4" sx={{ marginBottom: 2 }}>
              Désolé :(
            </DarkTypography>
            <DarkTypography level="h4">Cette page a expiré ou n'est plus valide.</DarkTypography>
          </>
        )}
      </Box>
    </Box>
  );
}
